import { Eye, EyeOff, ChartGantt } from 'lucide-react';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { getChartColor, STATUS_BAR_COLORS } from '../utils/colors';
import { DEFAULT_FILTERS } from '../utils/constants';
import { formatOriginalDateLabel } from '../utils/dateHelpers';

const OriginSourceOverview = ({
  overviewData,
  splitView,
  defaultFilters = DEFAULT_FILTERS,
  selectedDayIndex,
  chartLabels,
  clickedBarInfo,
  originalDateLabels,
  hiddenDatasets,
  setHiddenDatasets,
  isTooltip = false,
  isMobileOrTablet = false,
  setClickedBarInfo,
  onClose,
}) => {
  const containerRef = useRef(null);
  const barsContainerRef = useRef(null);
  const [maxBarWidth, setMaxBarWidth] = useState(406);

  useEffect(() => {
    const updateWidth = () => {
      if (barsContainerRef.current) {
        const containerWidth = barsContainerRef.current.offsetWidth;

        const availableWidth = containerWidth - 22;
        setMaxBarWidth(Math.max(200, availableWidth));
      }
    };

    updateWidth();

    let resizeObserver = null;
    if (barsContainerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(barsContainerRef.current);
    }

    window.addEventListener('resize', updateWidth);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const displayData =
    selectedDayIndex !== null && overviewData?.days?.[selectedDayIndex]
      ? overviewData.days[selectedDayIndex]
      : overviewData?.total || {};

  const maxValue = displayData.all || 0;
  const calculateWidth = (value) => {
    if (maxValue === 0) return 0;
    return Math.round((value / maxValue) * maxBarWidth);
  };

  const getColors = React.useCallback(
    (label) => getChartColor(label, splitView, 0, defaultFilters),
    [splitView, defaultFilters]
  );

  const handleToggleDataset = useCallback(
    (label) => {
      setHiddenDatasets((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(label)) {
          newSet.delete(label);
        } else {
          newSet.add(label);
        }
        return newSet;
      });
    },
    [setHiddenDatasets]
  );

  const barData = React.useMemo(() => {
    if (splitView === 'status') {
      return [
        {
          label: 'Scheduled',
          value: displayData.scheduled || 0,
          color: getColors('Scheduled'),
          backgroundColor: STATUS_BAR_COLORS.Scheduled.backgroundColor,
          borderColor: STATUS_BAR_COLORS.Scheduled.borderColor,
        },
        {
          label: 'Rescheduled',
          value: displayData.rescheduled || 0,
          color: getColors('Rescheduled'),
          backgroundColor: STATUS_BAR_COLORS.Rescheduled.backgroundColor,
          borderColor: STATUS_BAR_COLORS.Rescheduled.borderColor,
        },
        {
          label: 'Expired',
          value: displayData.expired || 0,
          color: getColors('Expired'),
          backgroundColor: STATUS_BAR_COLORS.Expired.backgroundColor,
          borderColor: STATUS_BAR_COLORS.Expired.borderColor,
        },
        {
          label: 'Canceled',
          value: displayData.cancelled || 0,
          color: getColors('Canceled'),
          backgroundColor: STATUS_BAR_COLORS.Canceled.backgroundColor,
          borderColor: STATUS_BAR_COLORS.Canceled.borderColor,
        },
        {
          label: 'Used',
          value: displayData.used || 0,
          color: getColors('Used'),
          backgroundColor: STATUS_BAR_COLORS.Used.backgroundColor,
          borderColor: STATUS_BAR_COLORS.Used.borderColor,
        },
        {
          label: 'Rejected',
          value: displayData.rejected || 0,
          color: getColors('Rejected'),
          backgroundColor: STATUS_BAR_COLORS.Rejected.backgroundColor,
          borderColor: STATUS_BAR_COLORS.Rejected.borderColor,
        },
      ];
    } else if (splitView === 'type') {
      const configs = overviewData?.datasetConfigs || [];
      return [
        { label: 'All', value: displayData.all || 0, color: getColors('All') },
        ...configs.map((config) => ({
          label: config.label,
          value: displayData[config.key] || 0,
          color: getColors(config.label),
        })),
      ];
    } else if (splitView === 'terminal') {
      return [
        { label: 'All', value: displayData.all || 0, color: getColors('All') },
        {
          label: 'Yusen',
          value: displayData.yusen || 0,
          color: getColors('Yusen'),
        },
        {
          label: 'Everport',
          value: displayData.everport || 0,
          color: getColors('Everport'),
        },
        {
          label: 'SSA Marine',
          value: displayData.ssaMarine || 0,
          color: getColors('SSA Marine'),
        },
        {
          label: 'Matson',
          value: displayData.matson || 0,
          color: getColors('Matson'),
        },
        {
          label: 'Trapac',
          value: displayData.trapac || 0,
          color: getColors('Trapac'),
        },
      ];
    } else {
      return [
        { label: 'All', value: displayData.all || 0, color: getColors('All') },
        {
          label: 'Autobook',
          value: displayData.autobook || 0,
          color: getColors('Autobook'),
        },
        {
          label: 'Manual',
          value: displayData.manual || 0,
          color: getColors('Manual'),
        },
        {
          label: 'External',
          value: displayData.external || 0,
          color: getColors('External'),
        },
      ];
    }
  }, [splitView, displayData, getColors, overviewData?.datasetConfigs]);

  const containerHeight = 183;
  const barHeight = 18;
  const numberOfBars = barData.length;
  const totalBarHeight = barHeight * numberOfBars;
  const totalGapSpace = containerHeight - totalBarHeight;
  const numberOfGaps = numberOfBars - 1;
  const gapSize = numberOfGaps > 0 ? totalGapSpace / numberOfGaps : 0;
  const dashedLineOffset = gapSize / 2;

  const getTitle = () => {
    if (splitView === 'status') {
      return 'Status';
    } else if (splitView === 'type') {
      return 'Delivery Type';
    } else if (splitView === 'terminal') {
      return 'Terminal';
    } else {
      return 'Origin Source';
    }
  };

  return (
    <div
      ref={containerRef}
      className="h-full bg-white border-solid border border-tertiary rounded-xl p-4 flex flex-col gap-4">
      <div className="flex items-center gap-2 pb-3 border-b border-gray-2000 border-solid border-l-0 border-r-0 border-t-0">
        <ChartGantt />
        <h3
          className={` font-bold text-primary-text-secondary m-0 ${
            isTooltip ? 'text-sm' : 'text-lg'
          }`}>
          {getTitle()}{' '}
          {selectedDayIndex !== null
            ? clickedBarInfo?.date
              ? formatOriginalDateLabel(clickedBarInfo.date)
              : originalDateLabels && originalDateLabels[selectedDayIndex]
              ? formatOriginalDateLabel(originalDateLabels[selectedDayIndex])
              : chartLabels?.[selectedDayIndex] || 'Overview'
            : 'Overview'}
        </h3>
      </div>

      <div className="relative" style={{ height: '199px' }}>
        <div className="flex h-full items-start" style={{ height: '183px' }}>
          <div
            className="flex flex-col text-xs font-bold justify-between flex-shrink-0 pr-6"
            style={{
              height: '183px',
              color: '#828898',
              width: '90px',
            }}>
            {barData.map((item, index) => (
              <span
                key={index}
                style={{ height: '18px', lineHeight: '18px' }}
                className="flex items-center gap-2">
                <span
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: item.backgroundColor || item.color,

                    display: 'inline-block',
                    borderRadius: '2px',
                    flexShrink: 0,
                  }}
                />
                {item.label}
              </span>
            ))}
          </div>

          <div
            className="flex flex-col justify-between flex-shrink-0 pr-1"
            style={{
              height: '183px',
            }}>
            {barData.map((item, index) => {
              const isHidden = hiddenDatasets.has(item.label);
              return (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleDataset(item.label);
                  }}
                  className="cursor-pointer hover:opacity-70 transition-opacity flex items-center"
                  style={{
                    padding: '2px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    background: 'transparent',
                    border: 'none',
                  }}
                  title={
                    isHidden ? 'Show line in chart' : 'Hide line in chart'
                  }>
                  {isHidden ? (
                    <EyeOff size={14} color="#525866" />
                  ) : (
                    <Eye size={14} color="#525866" />
                  )}
                </button>
              );
            })}
          </div>

          <div
            className="bg-border-primary w-0.5 flex-shrink-0"
            style={{
              height: '202px',
              marginTop: '-2px',
            }}
          />

          <div
            ref={barsContainerRef}
            className="flex flex-col justify-between flex-1 gap-2"
            style={{
              height: '183px',
              marginTop: '1px',
            }}>
            {barData.map((item, index) => {
              const isClicked =
                clickedBarInfo?.clickedSegment?.label === item.label;
              return (
                <div
                  key={index}
                  className="relative"
                  style={{ height: '18px' }}>
                  {index < barData.length - 1 && (
                    <div
                      className="absolute left-0 w-full h-px"
                      style={{
                        bottom: `-${dashedLineOffset}px`,
                        borderTop: '1px dashed #E8E8E8',
                      }}
                    />
                  )}
                  <div
                    className="rounded-tr rounded-br relative h-full w-full"
                    style={{
                      backgroundColor: '#F5F5F5',
                      border: isClicked ? '2px solid #3B82F6' : 'none',
                      boxShadow: isClicked
                        ? '0 0 0 1px rgba(59, 130, 246, 0.3), 0 2px 4px rgba(0, 0, 0, 0.15)'
                        : 'none',
                    }}>
                    <div
                      onClick={() => {
                        if (isMobileOrTablet) {
                          setClickedBarInfo({
                            date: clickedBarInfo.date,
                            clickedSegment: item,
                          });

                          if (onClose) {
                            onClose();
                            const myAppointments =
                              document.getElementById('my-appointments');
                            myAppointments.scrollIntoView({
                              behavior: 'smooth',
                            });
                          }
                        }
                      }}
                      className="rounded-tr rounded-br absolute left-0 top-0 h-full"
                      style={{
                        width: `${calculateWidth(item.value)}px`,
                        backgroundColor: item.backgroundColor || item.color,

                        borderRadius: '0 4px 4px 0',
                        opacity: isClicked ? 1 : 1,
                        boxShadow: isClicked
                          ? 'inset 0 1px 2px rgba(0, 0, 0, 0.1)'
                          : 'none',
                      }}
                    />
                    <span
                      className="absolute font-averta-bold text-primary-text text-11"
                      style={{
                        left: `${calculateWidth(item.value) + 8}px`,
                        top: index === 0 ? '2px' : '1px',
                        fontWeight: isClicked ? 'bold' : 'normal',
                      }}>
                      {item.value}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OriginSourceOverview;
